import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Link as MuiLink,
  Checkbox,
  FormControlLabel,
  AppBar,
  Toolbar,
} from "@mui/material";
import { Public as PublicIcon } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import PasswordTextField from "../components/PasswordTextField";

const steps = ["Create Account", "Find Partner", "Register for Tournament"];

export default function TournamentPlayerRegister() {
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Get invitation details from URL
  const invitationId = searchParams.get("invitationId");
  const partnerName = searchParams.get("partnerName");
  const recipientName = searchParams.get("recipientName");
  const tournamentId = searchParams.get("tournamentId");

  // Organization-aware colors
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const primaryColor = isPadelEagle ? "#743444" : currentOrg?.branding?.primaryColor || "#1a237e";
  const secondaryColor = isPadelEagle ? "#E49421" : currentOrg?.branding?.secondaryColor || "#283593";
  const backgroundColor = currentOrg?.branding?.backgroundColor || (isPadelEagle ? "#1a1a1a" : "#f5f5f5");

  // Form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    level: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [autoApproved, setAutoApproved] = useState(false);

  // Pre-fill name from URL if provided
  useEffect(() => {
    if (recipientName && !formData.firstName && !formData.lastName) {
      const spaceIdx = recipientName.indexOf(" ");
      setFormData(prev => ({
        ...prev,
        firstName: spaceIdx !== -1 ? recipientName.slice(0, spaceIdx) : recipientName,
        lastName: spaceIdx !== -1 ? recipientName.slice(spaceIdx + 1) : "",
      }));
    }
  }, [recipientName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Usernames must be usable on the login form: ignore characters that aren't allowed
    if (e.target.name === "username" && !/^[a-zA-Z0-9_-]*$/.test(e.target.value)) return;
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!agreedToPrivacy) {
      setError("You must agree to the privacy policy to continue");
      return;
    }

    if (formData.username.length < 3) {
      setError("Username must be at least 3 characters");
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setError("Username can only contain letters, numbers, hyphens, and underscores (no spaces)");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

      // Create player account request
      const response = await apiRequest(
        "/api/player-requests/account-requests",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: fullName,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email,
            phone: formData.phone,
            level: formData.level || null,
            username: formData.username,
            password: formData.password,
            requestType: "tournament",
            invitationId: invitationId || null,
            pendingTournamentId: tournamentId || null,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        // Clear form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          level: "",
          username: "",
          password: "",
          confirmPassword: "",
        });
        setAgreedToPrivacy(false);

        if (data.autoApproved) {
          // Account was instantly approved — tell player they can log in now
          setAutoApproved(true);
          // Always redirect to view-tournaments after login
          localStorage.setItem("redirectAfterLogin", "/view-tournaments");
          // If an invitation was completed, store the confirmation details for the post-login notification
          if (data.partnerName || data.tournamentName) {
            sessionStorage.setItem("registrationSuccess", JSON.stringify({
              partnerName: data.partnerName,
              tournamentName: data.tournamentName,
            }));
          }
          setSuccess("auto-approved");
          setActiveStep(1);
        } else {
          let successMessage = "Registration request submitted! Our team will review and approve your account shortly. You will receive an email confirmation.";
          if (invitationId && partnerName) {
            successMessage = `Registration request submitted! Once approved, you'll be automatically registered for the tournament with ${partnerName}. You will receive an email confirmation.`;
          } else if (tournamentId) {
            successMessage = "Registration request submitted! Your account request is linked to this tournament, so admins can still place or invite you when spots are available.";
          }
          setAutoApproved(false);
          setSuccess(successMessage);
          setActiveStep(1);
        }
      } else {
        const data = await response.json();
        setError(data.error || "Failed to submit registration");
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Failed to submit registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar position="static" elevation={2} sx={{ bgcolor: primaryColor }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isPadelEagle ? "Player Registration" : "Tournament Registration"}
          </Typography>
          <Button
            color="inherit"
            startIcon={<PublicIcon />}
            onClick={() => navigate("/")}
          >
            Home
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          background: isPadelEagle
            ? primaryColor
            : "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
          py: 4,
        }}
      >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: primaryColor, fontWeight: "bold" }}
            >
              {isPadelEagle ? "Player Registration" : "Tournament Player Registration"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isPadelEagle
                ? "Join Padel Eagle and showcase your skills"
                : `Join ${currentOrg?.name || "our tournaments"} and compete with the best players`}
            </Typography>
          </Box>

          {/* Stepper - Hide for Padel Eagle (single step) */}
          {!isPadelEagle && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && success !== "auto-approved" && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Step 1: Create Account */}
          {activeStep === 0 && (
            <Box component="form" onSubmit={handleSubmit}>
              {invitationId && partnerName && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    🎾 Tournament Invitation
                  </Typography>
                  <Typography variant="body2">
                    <strong>{partnerName}</strong> has invited you to join their team! Complete your registration below and you'll be automatically registered for the tournament once your account is approved.
                  </Typography>
                </Alert>
              )}

              <Typography variant="h6" gutterBottom sx={{ color: primaryColor }}>
                {isPadelEagle ? "Create Your Account" : "Step 1: Create Your Account"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Provide your details to create a player account. Your account
                will be reviewed by our team before approval.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name *"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name *"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </Box>

              <TextField
                fullWidth
                label="Email Address *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                helperText="You will receive login credentials at this email"
              />

              <TextField
                fullWidth
                label="Phone Number *"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Skill Level"
                name="level"
                type="number"
                value={formData.level}
                onChange={handleInputChange}
                inputProps={{
                  min: 1,
                  max: 5,
                  step: 0.1,
                }}
                sx={{ mb: 2 }}
                helperText="Optional - Rate your skill from 1 (beginner) to 5 (expert)"
              />

              <TextField
                fullWidth
                label="Username *"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                helperText="Letters, numbers, hyphens, and underscores only (no spaces)"
              />

              <PasswordTextField
                fullWidth
                label="Password *"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
                helperText="Minimum 8 characters"
              />

              <PasswordTextField
                fullWidth
                label="Confirm Password *"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    sx={{ 
                      color: primaryColor,
                      '&.Mui-checked': {
                        color: primaryColor,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <MuiLink
                      href="/privacy"
                      target="_blank"
                      sx={{ color: primaryColor, fontWeight: "bold" }}
                    >
                      Privacy Policy
                    </MuiLink>
                  </Typography>
                }
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: primaryColor,
                  "&:hover": { bgcolor: secondaryColor },
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                {loading ? "Submitting..." : "Submit Registration Request"}
              </Button>
            </Box>
          )}

          {/* Step 2: Confirmation */}
          {activeStep === 1 && (
            <Box>
              {autoApproved ? (
                <>
                  <Alert severity="success" icon={false} sx={{ mb: 3, textAlign: "center" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      🎉 Account Created!
                    </Typography>
                    <Typography variant="body2">
                      Your account has been created and is ready to use. You can log in right now and register for the tournament.
                    </Typography>
                    {invitationId && partnerName && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Your team registration with <strong>{partnerName}</strong> has been confirmed. You'll see the details once you log in.
                      </Typography>
                    )}
                  </Alert>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/player/login")}
                    sx={{
                      bgcolor: primaryColor,
                      "&:hover": { bgcolor: secondaryColor },
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    Log In Now
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom sx={{ color: primaryColor }}>
                    {isPadelEagle ? "What's Next?" : "Step 2: What's Next?"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Once your account is approved, you can log in with your chosen username and password. After logging in, you can:
                  </Typography>
                  <Box sx={{ mb: 3, pl: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • Browse the <strong>Partner Board</strong> to find available players
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • Send or accept partner invitations
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • Register as a team for upcoming tournaments
                    </Typography>
                  </Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    You'll receive an email notification once your account is approved. This usually takes 24-48 hours. Your username and password are already set — just wait for approval to log in!
                  </Alert>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/view-tournaments")}
                    sx={{
                      bgcolor: primaryColor,
                      "&:hover": { bgcolor: secondaryColor },
                      py: 1.5,
                    }}
                  >
                    View Upcoming Tournaments
                  </Button>
                </>
              )}
              <Button
                variant="text"
                fullWidth
                onClick={() => { setActiveStep(0); setAutoApproved(false); setSuccess(""); }}
                sx={{ mt: 2 }}
              >
                Submit Another Registration
              </Button>
            </Box>
          )}

          {/* Footer Links */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <MuiLink
                component="button"
                onClick={() => navigate("/player/login")}
                sx={{ color: isPadelEagle ? "#743444" : "#1a237e", fontWeight: "bold" }}
              >
                Login here
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Container>
      </Box>
    </Box>
  );
}
